import { useState, useMemo } from "react";
import { IonToolbar, IonFooter, IonButtons } from "@ionic/react";
import { useHistory, useLocation } from "react-router";
import { useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import { safeNavigate } from "../../utils/navigationHelper";
import { freeProgramMenuPath } from "../../utils/menuWeek";
import { normalizeCta } from "../CtaBar/CtaBar";
import { redirectToPremium } from "../../utils/premiumRedirect";
import { useAppPermission } from "../../hooks/useAppPermission";
import PermissionPopupModal from "../modals/PermissionPopupModal/PermissionPopupModal";
import UpcomingFreeProgramBar from "../UpcomingFreeProgramBar";
import {
  COMMUNITY_FEED_PATH,
  PERMISSION_ACTIONS,
} from "../../constants/permissionActions";
import "./MainFooter.css";

// Change this variable to switch between icon sets: 'phosphor' | 'remix' | 'fluent'
const ICON_SET = "remix";

const routesPhosphor = [
  { url: "/home", icon: "ph:house", iconFill: "ph:house-fill", name: "Home" },
  {
    url: "/community",
    icon: "ph:users",
    iconFill: "ph:users-fill",
    name: "Community",
  },
  {
    url: "/menu",
    // Active on /menu/<day> too: a FREE member's tab opens a dated week.
    activePaths: ["/menu"],
    icon: "ph:book-open",
    iconFill: "ph:book-open-fill",
    name: "Menu",
  },
  {
    url: "/ricette",
    icon: "ph:fork-knife",
    iconFill: "ph:fork-knife-fill",
    name: "Ricette",
  },
  {
    url: "/multimedia",
    icon: "ph:video-camera",
    iconFill: "ph:video-camera-fill",
    name: "Video",
    // The Video tab owns both the section (/multimedia, incl. sub-routes) and
    // the standalone video detail page (/video/:id) — which is reachable from
    // the feed's "Vai alla scheda video" link. Listing both keeps the tab
    // highlighted on the detail page, matching entry from the Video section.
    activePaths: ["/multimedia", "/video"],
  },
  {
    // Sfide (Challenges) — matches the icon used in MainMenu's
    // burger menu (mainMenu/menuRoutes.json -> "Sfide & Attività"
    // uses ri:trophy-line). The "trophy" semantic is consistent
    // across icon sets. Replaces the previous "Shop" entry; Shop
    // remains reachable from the burger menu.
    url: "/sfide",
    icon: "ph:trophy",
    iconFill: "ph:trophy-fill",
    name: "Sfide",
  },
];

const routesRemix = [
  {
    url: "/home",
    icon: "ri:home-line",
    iconFill: "ri:home-fill",
    name: "Home",
  },
  {
    url: "/community",
    icon: "ri:group-line",
    iconFill: "ri:group-fill",
    name: "Community",
  },
  {
    url: "/menu",
    // Active on /menu/<day> too: a FREE member's tab opens a dated week.
    activePaths: ["/menu"],
    icon: "ri:book-line",
    iconFill: "ri:book-fill",
    name: "Menu",
  },
  {
    url: "/ricette",
    icon: "ri:restaurant-line",
    iconFill: "ri:restaurant-fill",
    name: "Ricette",
  },
  {
    url: "/multimedia",
    icon: "ri:vidicon-line",
    iconFill: "ri:vidicon-fill",
    name: "Video",
    // The Video tab owns both the section (/multimedia, incl. sub-routes) and
    // the standalone video detail page (/video/:id) — which is reachable from
    // the feed's "Vai alla scheda video" link. Listing both keeps the tab
    // highlighted on the detail page, matching entry from the Video section.
    activePaths: ["/multimedia", "/video"],
  },
  {
    // Sfide (Challenges) — same icon as the burger menu uses for
    // "Sfide & Attività" (ri:trophy-line) so the user sees the same
    // glyph whether they tap the side menu or the bottom bar.
    url: "/sfide",
    icon: "ri:trophy-line",
    iconFill: "ri:trophy-fill",
    name: "Sfide",
  },
];

const routesFluent = [
  {
    url: "/home",
    icon: "fluent:home-24-regular",
    iconFill: "fluent:home-24-filled",
    name: "Home",
  },
  {
    url: "/community",
    icon: "fluent:people-24-regular",
    iconFill: "fluent:people-24-filled",
    name: "Community",
  },
  {
    url: "/menu",
    // Active on /menu/<day> too: a FREE member's tab opens a dated week.
    activePaths: ["/menu"],
    icon: "fluent:book-24-regular",
    iconFill: "fluent:book-24-filled",
    name: "Menu",
  },
  {
    url: "/ricette",
    icon: "fluent:food-24-regular",
    iconFill: "fluent:food-24-filled",
    name: "Ricette",
  },
  {
    url: "/multimedia",
    icon: "fluent:video-24-regular",
    iconFill: "fluent:video-24-filled",
    name: "Video",
    // The Video tab owns both the section (/multimedia, incl. sub-routes) and
    // the standalone video detail page (/video/:id) — which is reachable from
    // the feed's "Vai alla scheda video" link. Listing both keeps the tab
    // highlighted on the detail page, matching entry from the Video section.
    activePaths: ["/multimedia", "/video"],
  },
  {
    // Sfide (Challenges) — kept in sync with the other icon sets so
    // toggling ICON_SET above doesn't regress the bottom-bar entry.
    url: "/sfide",
    icon: "fluent:trophy-24-regular",
    iconFill: "fluent:trophy-24-filled",
    name: "Sfide",
  },
];

const MainFooter = () => {
  const history = useHistory();
  const location = useLocation();

  const {
    runIfAllowed: runIfViewFeedAllowed,
    isModalOpen: isViewFeedModalOpen,
    setIsModalOpen: setIsViewFeedModalOpen,
    popupText: viewFeedPopupText,
  } = useAppPermission(PERMISSION_ACTIONS.FEED_COMMUNITY_VIEW_FEED);

  // ── Redux: course CTA + subscription ─────────────────────────
  const courseData = useSelector(
    (s) =>
      s.course?.getCourse?.data?.course ?? s.course?.getCourse?.data ?? null,
  );
  const courseCta = courseData?.cta ?? null;
  const canCancel = courseData?.can_cancel !== false;
  const subscriptionName = useSelector(
    (s) =>
      s.user?.master_club_subscriber?.subscription?.subscription_type?.name,
  );
  const isFreeUser = subscriptionName === "free";
  const subscription = useSelector(
    (s) => s.user?.master_club_subscriber?.subscription ?? null,
  );
  const normalized = normalizeCta(courseCta);
  // Backend-driven post-free-week CTA: the profile endpoint returns `free_cta`
  // (admin-configured text + visibility) for a guest within the grace window —
  // until the Tuesday after the free week's Sunday end. This replaces the old
  // per-device localStorage snapshot, so the bar's text and visibility are
  // identical on every device and always reflect the backend text. Active free
  // users still get the lesson-gated CTA from the course endpoint (`normalized`).
  const backendFreeCta = useSelector(
    (s) => s.user?.master_club_subscriber?.free_cta ?? null,
  );
  const graceCta = useMemo(() => {
    if (isFreeUser || !backendFreeCta || backendFreeCta.visible === false) {
      return null;
    }
    return normalizeCta(backendFreeCta);
  }, [isFreeUser, backendFreeCta]);

  // Show the red CTA bar when: free user + CTA available + can_cancel is false,
  // OR within the post-free grace window. Hide on shopping-widget pages.
  const isOnShoppingPage = location.pathname.startsWith("/shopping-widget");

  // Enrolled in a FREE Program that starts more than a week away: still a
  // Guest, so the start date and "Annulla iscrizione" sit above the tab bar. It
  // takes the place of the grace-period CTA, which is about a FREE week the
  // member has already moved on from.
  const upcomingFreeProgram = useSelector(
    (s) => s.user?.master_club_subscriber?.upcoming_free_program ?? null,
  );
  const showUpcomingFree =
    !isFreeUser && !!upcomingFreeProgram?.start && !isOnShoppingPage;

  const effectiveCta = isFreeUser ? normalized : graceCta;
  const showGlobalCta =
    !!effectiveCta &&
    !isOnShoppingPage &&
    !showUpcomingFree &&
    (isFreeUser ? !canCancel : true);

  const routes =
    ICON_SET === "phosphor"
      ? routesPhosphor
      : ICON_SET === "remix"
      ? routesRemix
      : routesFluent;

  const isRouteActive = (route, pathname) => {
    // Routes with explicit `activePaths` (e.g. Video) are active on any of those
    // prefixes and their sub-routes; everything else keeps exact-match behavior.
    const prefixes = route.activePaths;
    if (prefixes && prefixes.length) {
      return prefixes.some(
        (p) => pathname === p || pathname.startsWith(p + "/"),
      );
    }
    return pathname === route.url;
  };

  const getIcon = (route) => {
    const isActive = isRouteActive(route, location.pathname) && !location.hash;
    return (
      <Icon
        icon={isActive ? route.iconFill : route.icon}
        width="24"
        height="24"
      />
    );
  };

  const handleRouteClick = (route, e) => {
    // Dev-mode diagnostic: log the tap target and the element that's
    // topmost at the click coordinates. If they differ, an overlay
    // is absorbing the click before the footer button handles it.
    // Stripped from production builds by the bundler's NODE_ENV
    // dead-code elimination.
    if (process.env.NODE_ENV === "development" && e) {
      const topEl =
        e.clientX != null && e.clientY != null
          ? document.elementFromPoint(e.clientX, e.clientY)
          : null;
      // eslint-disable-next-line no-console
      console.log("[MainFooter] tap on", route.name, {
        path: route.url,
        topmostAtPoint: topEl,
        currentTarget: e.currentTarget,
      });
    }

    // A FREE member's Menu always opens on their program week (today when it
    // is inside the program, otherwise its first day), never on a week the
    // program does not cover.
    const target =
      route.url === "/menu" && isFreeUser
        ? freeProgramMenuPath({ subscription }) ?? route.url
        : route.url;
    const navigate = () => safeNavigate(history, target, location.pathname);

    if (route.url === COMMUNITY_FEED_PATH) {
      const result = runIfViewFeedAllowed(navigate);
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.log("[MainFooter] community permission check ->", result);
      }
      return;
    }

    navigate();
  };

  return (
    <>
      {showUpcomingFree && (
        <UpcomingFreeProgramBar start={upcomingFreeProgram.start} />
      )}

      {/* ── Global red CTA bar — taps go straight to the paywall ──── */}
      {showGlobalCta && (
        <IonFooter className="global-cta-footer">
          <button
            className="global-cta-bar"
            onClick={() =>
              // The banner's label already comes from the CRM (`cta_text`); its
              // destination now comes from the same place, so the two can be
              // changed together without an app release.
              redirectToPremium({
                history,
                pathname: location.pathname,
                url: effectiveCta?.popup?.configured_button_url,
              })
            }
          >
            <span className="global-cta-bar__lock" aria-hidden="true">
              <Icon icon="mdi:lock" width="20" height="20" />
            </span>
            <span className="global-cta-bar__text">
              {effectiveCta?.text || "PROSEGUI CON IL PERCORSO COMPLETO"}
            </span>
            <span className="global-cta-bar__chevron" aria-hidden="true">
              {/* Triple-chevron (19giu) — gold fading, deepest on the left */}
              <svg
                width="34"
                height="20"
                viewBox="0 0 36 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 5l7 7-7 7"
                  stroke="#ffbe04"
                  strokeWidth="4.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15 5l7 7-7 7"
                  stroke="#ffce43"
                  strokeWidth="4.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M26 5l7 7-7 7"
                  stroke="#ffde81"
                  strokeWidth="4.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
        </IonFooter>
      )}

      {/* ── Tab bar ─────────────────────────────────────────────── */}
      <IonFooter id="main-footer">
        <IonToolbar>
          <IonButtons className="ion-justify-content-around">
            {routes.map((route, index) => (
              <div
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  handleRouteClick(route, e);
                }}
              >
                <div className="footerIcon">
                  {getIcon(route)}
                  <span className="capitalize-first-letter">{route.name}</span>
                </div>
              </div>
            ))}
          </IonButtons>
        </IonToolbar>
      </IonFooter>

      <PermissionPopupModal
        isOpen={isViewFeedModalOpen}
        setIsOpen={setIsViewFeedModalOpen}
        message={viewFeedPopupText}
      />
    </>
  );
};
export default MainFooter;
